/**
 * @fileoverview Task Tracker matrix: one top-level column per lesson (AR-539), own scroll container with
 * sticky header and student column (AR-895), framed by a card with the legend footer (AR-912 / AR-924).
 */

import { memo, useMemo } from 'react'
import { Box, Paper, Table } from '@mantine/core'
import { TaskTrackerLegend } from './TaskTrackerLegend'
import { TaskTrackerTableHead } from './TaskTrackerTableHead'
import { TaskTrackerStudentRow } from './TaskTrackerStudentRow'
import {
  FINAL_SUBCOLS,
  STUDENT_COLUMN_MIN_WIDTH,
  SUB_COLUMN_MIN_WIDTH,
  legendFooterStyle,
  scrollContainerStyle,
  tableStyle,
} from './tableHelpers'
import { buildActivitiesByLesson, buildCellMap, subColCount } from './tableModel'
import { groupActivitiesByLesson } from '../service/taskTrackerService'
import { countUploadableStudents } from '../uploadForClass'
import type { TaskTrackActivity, TaskTrackStudentRow as StudentRow } from '../types'

interface TaskTrackerTableProps {
  activities: TaskTrackActivity[]
  students: StudentRow[]
  /** Self-assessment cells are read-only (the student fills them elsewhere); only the teacher cell opens. */
  onTeacherAssessmentClick: (lessonId: string, studentId: string) => void
  onDeliverableCellClick: (activityId: string, studentId: string) => void
  onFinalAssessmentClick?: (studentId: string) => void
  /** When set, shows the "Upload" action under each Deliverable sub-header. */
  onUploadForClassClick?: (activityId: string) => void
  currentUserId?: string
}

export const TaskTrackerTable = memo(function TaskTrackerTable({
  activities,
  students,
  onTeacherAssessmentClick,
  onDeliverableCellClick,
  onFinalAssessmentClick,
  onUploadForClassClick,
  currentUserId,
}: TaskTrackerTableProps) {
  const lessons = useMemo(() => groupActivitiesByLesson(activities), [activities])
  const activitiesByLesson = useMemo(() => buildActivitiesByLesson(activities), [activities])
  const cellMap = useMemo(() => buildCellMap(students), [students])
  const totalLessonSubcols = useMemo(() => lessons.reduce((sum, l) => sum + subColCount(l), 0), [lessons])
  // AR-924: an "Upload" badge with nobody left to upload for is disabled (everyone submitted themselves).
  const uploadableByActivity = useMemo(
    () => Object.fromEntries(activities.map(activity => [activity.id, countUploadableStudents(students, activity.id)])),
    [activities, students],
  )

  const tableMinWidth =
    STUDENT_COLUMN_MIN_WIDTH + (totalLessonSubcols + FINAL_SUBCOLS) * SUB_COLUMN_MIN_WIDTH

  return (
    <Paper withBorder radius="md" sx={{ overflow: 'hidden' }}>
      <Box style={scrollContainerStyle}>
        <Table
          withColumnBorders
          horizontalSpacing="sm"
          verticalSpacing="md"
          style={{ ...tableStyle, minWidth: tableMinWidth }}
        >
          <TaskTrackerTableHead
            lessons={lessons}
            studentsCount={students.length}
            uploadableByActivity={uploadableByActivity}
            onUploadForClassClick={onUploadForClassClick}
          />
          <tbody>
            {students.map(student => (
              <TaskTrackerStudentRow
                key={student.id}
                student={student}
                lessons={lessons}
                activitiesByLesson={activitiesByLesson}
                cellMap={cellMap}
                totalColumns={totalLessonSubcols + FINAL_SUBCOLS}
                currentUserId={currentUserId}
                onTeacherAssessmentClick={onTeacherAssessmentClick}
                onDeliverableCellClick={onDeliverableCellClick}
                onFinalAssessmentClick={onFinalAssessmentClick}
              />
            ))}
          </tbody>
        </Table>
      </Box>
      <Box style={legendFooterStyle}>
        <TaskTrackerLegend />
      </Box>
    </Paper>
  )
})
